import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import SideMenu from '../components/SideMenu';
import { useClients } from '../context/ClientsContext';
import countries from 'i18n-iso-countries';
import enCountries from 'i18n-iso-countries/langs/en.json';
import ruCountries from 'i18n-iso-countries/langs/ru.json';
import { SUPPORTED_COUNTRY_ALPHA2 } from '../constants/countries';

countries.registerLocale(enCountries);
countries.registerLocale(ruCountries);

export default function Clients() {
  const { currentTheme, theme } = useTheme();
  const { clients, users, refreshClients, loading, error } = useClients();
  const [editing, setEditing] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({ name: '', country_alpha2: '', status: 'active' });
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', status: 'active', company_id: '' });
  const [userSaving, setUserSaving] = useState(false);
  const [userSaveError, setUserSaveError] = useState(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', status: 'active', company_id: '' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState(null);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({ name: '', country_alpha2: '', status: 'active' });
  const [addCountryMenuOpen, setAddCountryMenuOpen] = useState(false);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [createCompanyError, setCreateCompanyError] = useState(null);

  useEffect(() => {
    refreshClients?.();
  }, [refreshClients]);

  const rows = useMemo(() => {
    const list = Array.isArray(clients) ? clients : [];
    return list.map((c, idx) => {
      const id = c?.id || c?.client_id || c?.clientId || `${idx}`;
      const name = c?.name || c?.title || c?.company_name || c?.companyName || '—';
      const status = c?.status || '—';
      const country_code = c?.country_code ?? c?.countryCode ?? c?.country ?? null;
      return { id, name, status, country_code, raw: c };
    });
  }, [clients]);

  const companyById = useMemo(() => {
    const map = new Map();
    (Array.isArray(clients) ? clients : []).forEach((c) => {
      const cid = c?.id;
      if (cid != null && cid !== '') map.set(String(cid), c);
    });
    return map;
  }, [clients]);

  const userRows = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    return list.map((u, idx) => {
      const id = u?.id || u?.user_id || u?.userId || `${idx}`;
      const name = u?.name || '—';
      const email = u?.email || '—';
      const status = u?.status || '—';
      const company_id = u?.company_id ?? u?.companyId ?? null;
      const company =
        company_id != null && company_id !== '' ? companyById.get(String(company_id)) : null;
      const companyName = company ? (company?.name || '—') : null;
      const companyCountryCode = company?.country_code ?? company?.countryCode ?? company?.country ?? null;
      return { id, name, email, status, company_id, companyName, companyCountryCode, raw: u };
    });
  }, [users, companyById]);

  const toAlpha2 = (numeric) => {
    if (numeric == null) return null;
    const digits = String(numeric).replace(/[^\d]/g, '');
    if (!digits) return null;
    const n = digits.padStart(3, '0');
    try {
      return countries.numericToAlpha2(n);
    } catch {
      return null;
    }
  };

  const getCountryName = (alpha2) => {
    if (!alpha2) return null;
    const lng = (i18n?.language || 'en').toLowerCase().startsWith('ru') ? 'ru' : 'en';
    try {
      return countries.getName(alpha2, lng) || countries.getName(alpha2, 'en') || null;
    } catch {
      return null;
    }
  };

  const countryLabel = (alpha2) => {
    if (!alpha2) return '—';
    const name = getCountryName(alpha2);
    return name ? `${name} (${alpha2})` : alpha2;
  };

  const alpha2ToCountryCodeNumeric = (alpha2) => {
    if (!alpha2) return null;
    try {
      const num = countries.alpha2ToNumeric(String(alpha2).toUpperCase());
      if (num == null || num === '') return null;
      const n = parseInt(String(num), 10);
      return Number.isNaN(n) ? null : n;
    } catch {
      return null;
    }
  };

  const openEditor = (row) => {
    setEditingUser(null);
    setUserSaveError(null);
    setSaveError(null);
    setEditing(row);
    const raw = row?.raw || {};
    const name = raw?.name ?? raw?.company_name ?? raw?.companyName ?? row?.name ?? '';
    const country_numeric = raw?.country_code ?? raw?.countryCode ?? raw?.country ?? null;
    const country_alpha2 = toAlpha2(country_numeric) || '';
    const status = raw?.status ?? row?.status ?? 'active';
    setForm({
      name: name == null ? '' : String(name),
      country_alpha2,
      status: status === 'suspended' ? 'suspended' : 'active',
    });
    setCountryMenuOpen(false);
  };

  const closeEditor = () => {
    setEditing(null);
    setForm({ name: '', country_alpha2: '', status: 'active' });
    setCountryMenuOpen(false);
    setSaveError(null);
    setSaving(false);
  };

  const openAddCompany = () => {
    closeEditor();
    closeUserEditor();
    setCreateCompanyError(null);
    setNewCompanyForm({ name: '', country_alpha2: '', status: 'active' });
    setAddCountryMenuOpen(false);
    setAddCompanyOpen(true);
  };

  const openAddUser = () => {
    closeEditor();
    closeUserEditor();
    setCreateUserError(null);
    setNewUserForm({ name: '', email: '', status: 'active', company_id: '' });
    setAddUserOpen(true);
  };

  const closeAddUser = () => {
    setAddUserOpen(false);
    setNewUserForm({ name: '', email: '', status: 'active', company_id: '' });
    setCreateUserError(null);
    setCreatingUser(false);
  };

  const closeAddCompany = () => {
    setAddCompanyOpen(false);
    setNewCompanyForm({ name: '', country_alpha2: '', status: 'active' });
    setAddCountryMenuOpen(false);
    setCreateCompanyError(null);
    setCreatingCompany(false);
  };

  const saveNewCompany = async () => {
    const nameTrimmed = String(newCompanyForm.name || '').trim();
    if (!nameTrimmed) return;

    const country_code = alpha2ToCountryCodeNumeric(newCompanyForm.country_alpha2);
    if (country_code == null) {
      setCreateCompanyError('Invalid country');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setCreateCompanyError('Missing access token');
      return;
    }

    const clientCoreApiUrl = process.env.REACT_APP_CLIENT_CORE_API_URL || '';
    const url = `${clientCoreApiUrl}/v1/clients/companies`;

    setCreatingCompany(true);
    setCreateCompanyError(null);

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: nameTrimmed,
          country_code,
          status: newCompanyForm.status === 'suspended' ? 'suspended' : 'active',
        }),
      });

      if (res.ok) {
        await refreshClients?.();
        closeAddCompany();
        return;
      }

      let message = `Create failed (${res.status})`;
      try {
        const data = await res.json();
        if (data?.detail) message = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        else if (data?.message) message = data.message;
      } catch {
        /* ignore */
      }
      setCreateCompanyError(message);
    } catch (e) {
      console.error('Company create failed:', e);
      setCreateCompanyError('Network error');
    } finally {
      setCreatingCompany(false);
    }
  };

  const openUserEditor = (row) => {
    setEditing(null);
    setCountryMenuOpen(false);
    setSaveError(null);
    setUserSaveError(null);
    setEditingUser(row);
    const raw = row?.raw || {};
    const name = raw?.name ?? row?.name ?? '';
    const email = raw?.email ?? row?.email ?? '';
    const status = raw?.status ?? row?.status ?? 'active';
    const normalizedStatus = ['active', 'invited', 'disabled'].includes(status) ? status : 'active';
    const company_id = raw?.company_id ?? raw?.companyId ?? '';
    setUserForm({
      name: name == null ? '' : String(name),
      email: email == null ? '' : String(email),
      status: normalizedStatus,
      company_id: company_id == null || company_id === '' ? '' : String(company_id),
    });
  };

  const closeUserEditor = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', status: 'active', company_id: '' });
    setUserSaveError(null);
    setUserSaving(false);
  };

  const saveUser = async () => {
    const nameTrimmed = String(userForm.name || '').trim();
    const emailTrimmed = String(userForm.email || '').trim();
    if (!nameTrimmed || !emailTrimmed || !editingUser) return;

    const userId = editingUser?.raw?.id ?? editingUser?.id;
    if (!userId) {
      setUserSaveError('Missing user id');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setUserSaveError('Missing access token');
      return;
    }

    const clientCoreApiUrl = process.env.REACT_APP_CLIENT_CORE_API_URL || '';
    const url = `${clientCoreApiUrl}/v1/clients/users/${encodeURIComponent(userId)}`;

    setUserSaving(true);
    setUserSaveError(null);

    try {
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: nameTrimmed,
          email: emailTrimmed,
          status: userForm.status,
          company_id: String(userForm.company_id || '').trim() || null,
        }),
      });

      if (res.ok) {
        await refreshClients?.();
        closeUserEditor();
        return;
      }

      let message = `Save failed (${res.status})`;
      try {
        const data = await res.json();
        if (data?.detail) message = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        else if (data?.message) message = data.message;
      } catch {
        /* ignore */
      }
      setUserSaveError(message);
    } catch (e) {
      console.error('User save failed:', e);
      setUserSaveError('Network error');
    } finally {
      setUserSaving(false);
    }
  };

  const saveNewUser = async () => {
    const nameTrimmed = String(newUserForm.name || '').trim();
    const emailTrimmed = String(newUserForm.email || '').trim();
    if (!nameTrimmed || !emailTrimmed) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setCreateUserError('Missing access token');
      return;
    }

    const clientCoreApiUrl = process.env.REACT_APP_CLIENT_CORE_API_URL || '';
    const url = `${clientCoreApiUrl}/v1/clients/users`;

    setCreatingUser(true);
    setCreateUserError(null);

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: nameTrimmed,
          email: emailTrimmed,
          status: newUserForm.status,
          company_id: String(newUserForm.company_id || '').trim() || null,
        }),
      });

      if (res.ok) {
        await refreshClients?.();
        closeAddUser();
        return;
      }

      let message = `Create failed (${res.status})`;
      try {
        const data = await res.json();
        if (data?.detail) message = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        else if (data?.message) message = data.message;
      } catch {
        /* ignore */
      }
      setCreateUserError(message);
    } catch (e) {
      console.error('User create failed:', e);
      setCreateUserError('Network error');
    } finally {
      setCreatingUser(false);
    }
  };

  const saveCompany = async () => {
    const nameTrimmed = String(form.name || '').trim();
    if (!nameTrimmed || !editing) return;

    const companyId = editing?.raw?.id ?? editing?.id;
    if (!companyId) {
      setSaveError('Missing company id');
      return;
    }

    const country_code =
      alpha2ToCountryCodeNumeric(form.country_alpha2) ??
      (() => {
        const raw = editing?.raw || {};
        const n = raw.country_code ?? raw.countryCode ?? raw.country;
        if (n == null || n === '') return null;
        const parsed = parseInt(String(n).replace(/[^\d]/g, ''), 10);
        return Number.isNaN(parsed) ? null : parsed;
      })();

    if (country_code == null) {
      setSaveError('Invalid country');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setSaveError('Missing access token');
      return;
    }

    const clientCoreApiUrl = process.env.REACT_APP_CLIENT_CORE_API_URL || '';
    const url = `${clientCoreApiUrl}/v1/clients/companies/${encodeURIComponent(companyId)}`;

    setSaving(true);
    setSaveError(null);

    try {
      const res = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: nameTrimmed,
          country_code,
          status: form.status === 'suspended' ? 'suspended' : 'active',
        }),
      });

      if (res.ok) {
        await refreshClients?.();
        closeEditor();
        return;
      }

      let message = `Save failed (${res.status})`;
      try {
        const data = await res.json();
        if (data?.detail) message = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        else if (data?.message) message = data.message;
      } catch {
        /* ignore */
      }
      setSaveError(message);
    } catch (e) {
      console.error('Company save failed:', e);
      setSaveError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const deleteCompany = async () => {
    if (!editing) return;

    const companyId = editing?.raw?.id ?? editing?.id;
    if (!companyId) {
      setSaveError('Missing company id');
      return;
    }

    const companyName = editing?.raw?.name ?? editing?.name;

    const ok = window.confirm(`${t('confirmRemoveCompany') || 'Delete this company?'}\n\nName: ${companyName}`);
    if (!ok) return;

    const token = localStorage.getItem('token');
    if (!token) {
      setSaveError('Missing access token');
      return;
    }

    const clientCoreApiUrl = process.env.REACT_APP_CLIENT_CORE_API_URL || '';
    const url = `${clientCoreApiUrl}/v1/clients/companies/${encodeURIComponent(companyId)}`;

    setSaving(true);
    setSaveError(null);

    try {
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        await refreshClients?.();
        closeEditor();
        return;
      }

      let message = `Delete failed (${res.status})`;
      try {
        const data = await res.json();
        if (data?.detail) message = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        else if (data?.message) message = data.message;
      } catch {
        /* ignore */
      }
      setSaveError(message);
    } catch (e) {
      console.error('Company delete failed:', e);
      setSaveError('Network error');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!editing && !editingUser && !addCompanyOpen && !addUserOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (addCompanyOpen) {
        setAddCountryMenuOpen(false);
        closeAddCompany();
      } else if (addUserOpen) {
        closeAddUser();
      } else if (editing) {
        setCountryMenuOpen(false);
        closeEditor();
      } else if (editingUser) {
        closeUserEditor();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editing, editingUser, addCompanyOpen, addUserOpen]);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: currentTheme.background }}>
      <SideMenu currentTheme={currentTheme} theme={theme} />

      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
          padding: 24,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h1 style={{ marginTop: 0, marginBottom: 0, color: currentTheme.text }}>{t('clients')}</h1>
        </div>

        {error && !loading && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 12,
              background: theme === 'light' ? '#FEF2F2' : 'rgba(239, 68, 68, 0.12)',
              border: `1px solid ${theme === 'light' ? '#FECACA' : 'rgba(239, 68, 68, 0.25)'}`,
              color: currentTheme.text,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            marginTop: 16,
            borderRadius: 12,
            background: currentTheme.surface,
            border: `1px solid ${currentTheme.border}`,
            boxShadow: currentTheme.cardShadow,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: 12,
              borderBottom: `1px solid ${currentTheme.border}`,
              background: theme === 'light' ? '#F9FAFB' : 'rgba(255,255,255,0.04)',
              color: currentTheme.text,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: currentTheme.textSecondary, marginBottom: 2 }}>{t('companies')}</div>
              <div>{t('total')}: {rows.length}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={openAddCompany}
                disabled={loading}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: `1px solid ${currentTheme.border}`,
                  background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.06)',
                  color: currentTheme.text,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                }}
              >
                {t('add')}
              </button>
              <button
                type="button"
                onClick={() => refreshClients?.()}
                disabled={loading}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: 'none',
                  background: theme === 'light' ? '#4F46E5' : '#6366F1',
                  color: '#fff',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                }}
              >
                {loading ? t('loading') +'...' : t('refresh')}
              </button>
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}` }}>
                {t('name')}
                </th>
                <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}`, width: 120 }}>
                  {t('country')}
                </th>
                <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}`, width: 160 }}>
                  {t('status')}
                </th>
                <th style={{ textAlign: 'right', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}`, width: 110 }}>
                {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.id}>
                  <td style={{ padding: 12, borderBottom: idx === rows.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', color: currentTheme.text }}>
                    {row.name}
                  </td>
                  <td style={{ padding: 12, borderBottom: idx === rows.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top' }}>
                    {(() => {
                      const alpha2 = toAlpha2(row.country_code);
                      if (!alpha2) return <span style={{ color: currentTheme.textSecondary }}>—</span>;
                      const code = String(alpha2).toLowerCase();
                      const countryName = getCountryName(alpha2);
                      const title = countryName ? `${countryName} (${alpha2})` : alpha2;
                      return (
                        <span
                          className={`fi fi-${code}`}
                          aria-hidden="true"
                          title={title}
                        />
                      );
                    })()}
                  </td>
                  <td style={{ padding: 12, borderBottom: idx === rows.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', color: currentTheme.textSecondary }}>
                    {row.status}
                  </td>
                  <td style={{ padding: 12, borderBottom: idx === rows.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => openEditor(row)}
                      aria-label={t('editCompany')}
                      title={t('editCompany')}
                      style={{
                        height: 30,
                        width: 30,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 10,
                        border: `1px solid ${currentTheme.border}`,
                        background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                        color: currentTheme.textSecondary,
                        cursor: 'pointer',
                      }}
                    >
                      <span className="oi oi-ellipses" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} style={{ padding: 12, color: currentTheme.textSecondary }}>
                    No clients
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            marginTop: 16,
            borderRadius: 12,
            background: currentTheme.surface,
            border: `1px solid ${currentTheme.border}`,
            boxShadow: currentTheme.cardShadow,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: 12,
              borderBottom: `1px solid ${currentTheme.border}`,
              background: theme === 'light' ? '#F9FAFB' : 'rgba(255,255,255,0.04)',
              color: currentTheme.text,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: currentTheme.textSecondary, marginBottom: 2 }}>{t('users')}</div>
              <div>{t('total')}: {userRows.length}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={openAddUser}
                disabled={loading}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: `1px solid ${currentTheme.border}`,
                  background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.06)',
                  color: currentTheme.text,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                }}
              >
                {t('add')}
              </button>
              <button
                type="button"
                onClick={() => refreshClients?.()}
                disabled={loading}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: 'none',
                  background: theme === 'light' ? '#4F46E5' : '#6366F1',
                  color: '#fff',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                }}
              >
                {loading ? `${t('loading')}...` : t('refresh')}
              </button>
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}` }}>
                  {t('username')}
                </th>
                <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}` }}>
                  {t('email')}
                </th>
                <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}` }}>
                  {t('company')}
                </th>
                <th style={{ textAlign: 'left', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}`, width: 140 }}>
                  {t('status')}
                </th>
                <th style={{ textAlign: 'right', padding: 12, fontSize: 12, color: currentTheme.textSecondary, borderBottom: `1px solid ${currentTheme.border}`, width: 110 }}>
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {userRows.map((row, idx) => (
                <tr key={row.id}>
                  <td style={{ padding: 12, borderBottom: idx === userRows.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', color: currentTheme.text }}>
                    {row.name}
                  </td>
                  <td style={{ padding: 12, borderBottom: idx === userRows.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', color: currentTheme.textSecondary }}>
                    {row.email}
                  </td>
                  <td style={{ padding: 12, borderBottom: idx === userRows.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', color: currentTheme.text }}>
                    {row.companyName != null ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        {(() => {
                          const alpha2 = toAlpha2(row.companyCountryCode);
                          if (!alpha2) return null;
                          const code = String(alpha2).toLowerCase();
                          const cname = getCountryName(alpha2);
                          const title = cname ? `${cname} (${alpha2})` : alpha2;
                          return (
                            <span
                              className={`fi fi-${code}`}
                              aria-hidden="true"
                              title={title}
                              style={{ flexShrink: 0 }}
                            />
                          );
                        })()}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.companyName}
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: currentTheme.textSecondary }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: 12, borderBottom: idx === userRows.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', color: currentTheme.textSecondary }}>
                    {row.status}
                  </td>
                  <td style={{ padding: 12, borderBottom: idx === userRows.length - 1 ? 'none' : `1px solid ${currentTheme.border}`, verticalAlign: 'top', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => openUserEditor(row)}
                      aria-label={t('editUser')}
                      title={t('editUser')}
                      style={{
                        height: 30,
                        width: 30,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 10,
                        border: `1px solid ${currentTheme.border}`,
                        background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                        color: currentTheme.textSecondary,
                        cursor: 'pointer',
                      }}
                    >
                      <span className="oi oi-ellipses" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
              {userRows.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} style={{ padding: 12, color: currentTheme.textSecondary }}>
                    {t('No data')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {addCompanyOpen && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: theme === 'light' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              zIndex: 1000,
            }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeAddCompany();
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 560,
                borderRadius: 14,
                background: currentTheme.surface,
                border: `1px solid ${currentTheme.border}`,
                boxShadow: currentTheme.cardShadow,
                color: currentTheme.text,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ fontWeight: 800 }}>{t('addCompany')}</div>
                <button
                  type="button"
                  onClick={closeAddCompany}
                  style={{
                    height: 30,
                    width: 30,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 10,
                    border: `1px solid ${currentTheme.border}`,
                    background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                    color: currentTheme.textSecondary,
                    cursor: 'pointer',
                  }}
                  aria-label="Close"
                  title="Close"
                >
                  ×
                </button>
              </div>

              <div
                style={{
                  marginTop: 12,
                  display: 'grid',
                  gap: 12,
                  gridTemplateColumns: '1fr 1fr',
                }}
              >
                <label style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('name')}</div>
                  <input
                    value={newCompanyForm.name}
                    onChange={(e) => setNewCompanyForm((f) => ({ ...f, name: e.target.value }))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('country')}</div>
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setAddCountryMenuOpen((v) => !v)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 12,
                        border: `1px solid ${currentTheme.border}`,
                        background: theme === 'light' ? '#FFFFFF' : currentTheme.surface,
                        color: currentTheme.text,
                        outline: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                      }}
                      aria-haspopup="listbox"
                      aria-expanded={addCountryMenuOpen}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        {newCompanyForm.country_alpha2 ? (
                          <span
                            className={`fi fi-${String(newCompanyForm.country_alpha2).toLowerCase()}`}
                            aria-hidden="true"
                            title={countryLabel(newCompanyForm.country_alpha2)}
                          />
                        ) : (
                          <span style={{ color: currentTheme.textSecondary }}>—</span>
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {newCompanyForm.country_alpha2
                            ? countryLabel(newCompanyForm.country_alpha2)
                            : t('select')}
                        </span>
                      </span>
                      <span className="oi oi-caret-bottom" aria-hidden="true" />
                    </button>

                    {addCountryMenuOpen && (
                      <div
                        role="listbox"
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          left: 0,
                          right: 0,
                          maxHeight: 280,
                          overflow: 'auto',
                          borderRadius: 12,
                          border: `1px solid ${currentTheme.border}`,
                          background: theme === 'light' ? '#FFFFFF' : currentTheme.surface,
                          boxShadow: currentTheme.cardShadow,
                          zIndex: 10,
                        }}
                      >
                        {SUPPORTED_COUNTRY_ALPHA2.map((alpha2) => {
                          const selected = alpha2 === newCompanyForm.country_alpha2;
                          return (
                            <button
                              key={alpha2}
                              type="button"
                              role="option"
                              aria-selected={selected}
                              onClick={() => {
                                setNewCompanyForm((f) => ({ ...f, country_alpha2: alpha2 }));
                                setAddCountryMenuOpen(false);
                              }}
                              style={{
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                cursor: 'pointer',
                                background: selected
                                  ? (theme === 'light' ? '#EEF2FF' : 'rgba(99, 102, 241, 0.18)')
                                  : 'transparent',
                                color: currentTheme.text,
                                padding: '10px 12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                              }}
                              title={countryLabel(alpha2)}
                            >
                              <span className={`fi fi-${alpha2.toLowerCase()}`} aria-hidden="true" />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {countryLabel(alpha2)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('status')}</div>
                  <select
                    value={newCompanyForm.status}
                    onChange={(e) => setNewCompanyForm((f) => ({ ...f, status: e.target.value }))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : currentTheme.surface,
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  >
                    <option value="active">active</option>
                    <option value="suspended">suspended</option>
                  </select>
                </label>
              </div>

              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {createCompanyError && (
                  <div
                    style={{
                      padding: 10,
                      borderRadius: 12,
                      background: theme === 'light' ? '#FEF2F2' : 'rgba(239, 68, 68, 0.12)',
                      border: `1px solid ${theme === 'light' ? '#FECACA' : 'rgba(239, 68, 68, 0.25)'}`,
                      color: currentTheme.text,
                      fontSize: 13,
                    }}
                  >
                    {createCompanyError}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                  <button
                    type="button"
                    onClick={closeAddCompany}
                    disabled={creatingCompany}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      cursor: creatingCompany ? 'not-allowed' : 'pointer',
                      opacity: creatingCompany ? 0.7 : 1,
                    }}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={
                      creatingCompany ||
                      !String(newCompanyForm.name || '').trim() ||
                      !newCompanyForm.country_alpha2
                    }
                    onClick={saveNewCompany}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: 'none',
                      background: theme === 'light' ? '#4F46E5' : '#6366F1',
                      color: '#fff',
                      cursor:
                        creatingCompany ||
                        !String(newCompanyForm.name || '').trim() ||
                        !newCompanyForm.country_alpha2
                          ? 'not-allowed'
                          : 'pointer',
                      opacity:
                        creatingCompany ||
                        !String(newCompanyForm.name || '').trim() ||
                        !newCompanyForm.country_alpha2
                          ? 0.7
                          : 1,
                    }}
                  >
                    {creatingCompany ? `${t('create')}…` : t('create')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {addUserOpen && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: theme === 'light' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              zIndex: 1000,
            }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeAddUser();
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 560,
                borderRadius: 14,
                background: currentTheme.surface,
                border: `1px solid ${currentTheme.border}`,
                boxShadow: currentTheme.cardShadow,
                color: currentTheme.text,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ fontWeight: 800 }}>{t('add')} {t('users')}</div>
                <button
                  type="button"
                  onClick={closeAddUser}
                  style={{
                    height: 30,
                    width: 30,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 10,
                    border: `1px solid ${currentTheme.border}`,
                    background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                    color: currentTheme.textSecondary,
                    cursor: 'pointer',
                  }}
                  aria-label="Close"
                  title="Close"
                >
                  ×
                </button>
              </div>

              <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('username')}</div>
                  <input
                    value={newUserForm.name}
                    onChange={(e) => setNewUserForm((f) => ({ ...f, name: e.target.value }))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('email')}</div>
                  <input
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm((f) => ({ ...f, email: e.target.value }))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('company')}</div>
                  <select
                    value={newUserForm.company_id}
                    onChange={(e) => setNewUserForm((f) => ({ ...f, company_id: e.target.value }))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : currentTheme.surface,
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  >
                    <option value="">{t('noCompany')}</option>
                    {(Array.isArray(clients) ? clients : [])
                      .filter((c) => c?.id != null && c?.id !== '')
                      .map((c) => {
                        const cid = String(c.id);
                        const label = c?.name || cid;
                        return (
                          <option key={cid} value={cid}>
                            {label}
                          </option>
                        );
                      })}
                  </select>
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('status')}</div>
                  <select
                    value={newUserForm.status}
                    onChange={(e) => setNewUserForm((f) => ({ ...f, status: e.target.value }))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : currentTheme.surface,
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  >
                    <option value="active">{t('Active')}</option>
                    <option value="invited">{t('invited')}</option>
                    <option value="disabled">{t('disabled')}</option>
                  </select>
                </label>
              </div>

              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {createUserError && (
                  <div
                    style={{
                      padding: 10,
                      borderRadius: 12,
                      background: theme === 'light' ? '#FEF2F2' : 'rgba(239, 68, 68, 0.12)',
                      border: `1px solid ${theme === 'light' ? '#FECACA' : 'rgba(239, 68, 68, 0.25)'}`,
                      color: currentTheme.text,
                      fontSize: 13,
                    }}
                  >
                    {createUserError}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                  <button
                    type="button"
                    onClick={closeAddUser}
                    disabled={creatingUser}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      cursor: creatingUser ? 'not-allowed' : 'pointer',
                      opacity: creatingUser ? 0.7 : 1,
                    }}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={
                      creatingUser ||
                      !String(newUserForm.name || '').trim() ||
                      !String(newUserForm.email || '').trim()
                    }
                    onClick={saveNewUser}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: 'none',
                      background: theme === 'light' ? '#4F46E5' : '#6366F1',
                      color: '#fff',
                      cursor:
                        creatingUser ||
                        !String(newUserForm.name || '').trim() ||
                        !String(newUserForm.email || '').trim()
                          ? 'not-allowed'
                          : 'pointer',
                      opacity:
                        creatingUser ||
                        !String(newUserForm.name || '').trim() ||
                        !String(newUserForm.email || '').trim()
                          ? 0.7
                          : 1,
                    }}
                  >
                    {creatingUser ? `${t('create')}…` : t('create')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!!editing && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: theme === 'light' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              zIndex: 1000,
            }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeEditor();
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 860,
                borderRadius: 14,
                background: currentTheme.surface,
                border: `1px solid ${currentTheme.border}`,
                boxShadow: currentTheme.cardShadow,
                color: currentTheme.text,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ fontWeight: 800 }}>{editing?.name || editing?.id}</div>
                <button
                  type="button"
                  onClick={closeEditor}
                  style={{
                    height: 30,
                    width: 30,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 10,
                    border: `1px solid ${currentTheme.border}`,
                    background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                    color: currentTheme.textSecondary,
                    cursor: 'pointer',
                  }}
                  aria-label="Close"
                  title="Close"
                >
                  ×
                </button>
              </div>

              <div
                style={{
                  marginTop: 12,
                  display: 'grid',
                  gap: 12,
                  gridTemplateColumns: '1fr 1fr',
                }}
              >
                <label style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('name')}</div>
                  <input
                    value={form.name}
                    onChange={(e) => {
                      const value = e.target.value;
                      setForm((f) => ({ ...f, name: value }));
                      setEditing((prev) => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          name: value,
                          raw: { ...(prev.raw || {}), name: value },
                        };
                      });
                    }}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('country')}</div>
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setCountryMenuOpen((v) => !v)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 12,
                        border: `1px solid ${currentTheme.border}`,
                        background: theme === 'light' ? '#FFFFFF' : currentTheme.surface,
                        color: currentTheme.text,
                        outline: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                      }}
                      aria-haspopup="listbox"
                      aria-expanded={countryMenuOpen}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        {form.country_alpha2 ? (
                          <span
                            className={`fi fi-${String(form.country_alpha2).toLowerCase()}`}
                            aria-hidden="true"
                            title={countryLabel(form.country_alpha2)}
                          />
                        ) : (
                          <span style={{ color: currentTheme.textSecondary }}>—</span>
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {form.country_alpha2 ? countryLabel(form.country_alpha2) : t('select')}
                        </span>
                      </span>
                      <span className="oi oi-caret-bottom" aria-hidden="true" />
                    </button>

                    {countryMenuOpen && (
                      <div
                        role="listbox"
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          left: 0,
                          right: 0,
                          maxHeight: 280,
                          overflow: 'auto',
                          borderRadius: 12,
                          border: `1px solid ${currentTheme.border}`,
                          background: theme === 'light' ? '#FFFFFF' : currentTheme.surface,
                          boxShadow: currentTheme.cardShadow,
                          zIndex: 10,
                        }}
                      >
                        {SUPPORTED_COUNTRY_ALPHA2.map((alpha2) => {
                          const selected = alpha2 === form.country_alpha2;
                          return (
                            <button
                              key={alpha2}
                              type="button"
                              role="option"
                              aria-selected={selected}
                              onClick={() => {
                                setForm((f) => ({ ...f, country_alpha2: alpha2 }));
                                setCountryMenuOpen(false);
                              }}
                              style={{
                                width: '100%',
                                textAlign: 'left',
                                border: 'none',
                                cursor: 'pointer',
                                background: selected
                                  ? (theme === 'light' ? '#EEF2FF' : 'rgba(99, 102, 241, 0.18)')
                                  : 'transparent',
                                color: currentTheme.text,
                                padding: '10px 12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                              }}
                              title={countryLabel(alpha2)}
                            >
                              <span className={`fi fi-${alpha2.toLowerCase()}`} aria-hidden="true" />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {countryLabel(alpha2)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('status')}</div>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : currentTheme.surface,
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  >
                    <option value="active">active</option>
                    <option value="suspended">suspended</option>
                  </select>
                </label>
              </div>

              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {saveError && (
                  <div
                    style={{
                      padding: 10,
                      borderRadius: 12,
                      background: theme === 'light' ? '#FEF2F2' : 'rgba(239, 68, 68, 0.12)',
                      border: `1px solid ${theme === 'light' ? '#FECACA' : 'rgba(239, 68, 68, 0.25)'}`,
                      color: currentTheme.text,
                      fontSize: 13,
                    }}
                  >
                    {saveError}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <button
                    type="button"
                    onClick={deleteCompany}
                    disabled={saving}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: 'none',
                      background: theme === 'light' ? '#DC2626' : 'rgba(239, 68, 68, 0.85)',
                      color: '#fff',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.7 : 1,
                    }}
                  >
                    Delete
                  </button>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                  <button
                    type="button"
                    onClick={closeEditor}
                    disabled={saving}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.7 : 1,
                    }}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={saving || !String(form.name || '').trim()}
                    onClick={saveCompany}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: 'none',
                      background: theme === 'light' ? '#4F46E5' : '#6366F1',
                      color: '#fff',
                      cursor: saving || !String(form.name || '').trim() ? 'not-allowed' : 'pointer',
                      opacity: saving || !String(form.name || '').trim() ? 0.7 : 1,
                    }}
                  >
                    {saving ? `${t('save')}…` : t('save')}
                  </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!!editingUser && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: theme === 'light' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              zIndex: 1000,
            }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeUserEditor();
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 560,
                borderRadius: 14,
                background: currentTheme.surface,
                border: `1px solid ${currentTheme.border}`,
                boxShadow: currentTheme.cardShadow,
                color: currentTheme.text,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ fontWeight: 800 }}>{editingUser?.name || editingUser?.email || editingUser?.id}</div>
                <button
                  type="button"
                  onClick={closeUserEditor}
                  style={{
                    height: 30,
                    width: 30,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 10,
                    border: `1px solid ${currentTheme.border}`,
                    background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                    color: currentTheme.textSecondary,
                    cursor: 'pointer',
                  }}
                  aria-label="Close"
                  title="Close"
                >
                  ×
                </button>
              </div>

              <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('username')}</div>
                  <input
                    value={userForm.name}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUserForm((f) => ({ ...f, name: value }));
                      setEditingUser((prev) => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          name: value,
                          raw: { ...(prev.raw || {}), name: value },
                        };
                      });
                    }}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('email')}</div>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUserForm((f) => ({ ...f, email: value }));
                      setEditingUser((prev) => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          email: value,
                          raw: { ...(prev.raw || {}), email: value },
                        };
                      });
                    }}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('company')}</div>
                  <select
                    value={userForm.company_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUserForm((f) => ({ ...f, company_id: value }));
                      setEditingUser((prev) => {
                        if (!prev) return prev;
                        const cid = value || null;
                        return {
                          ...prev,
                          raw: { ...(prev.raw || {}), company_id: cid },
                        };
                      });
                    }}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : currentTheme.surface,
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  >
                    <option value="">{t('noCompany')}</option>
                    {(Array.isArray(clients) ? clients : [])
                      .filter((c) => c?.id != null && c?.id !== '')
                      .map((c) => {
                        const cid = String(c.id);
                        const label = c?.name || cid;
                        return (
                          <option key={cid} value={cid}>
                            {label}
                          </option>
                        );
                      })}
                  </select>
                </label>

                <label style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 12, color: currentTheme.textSecondary }}>{t('status')}</div>
                  <select
                    value={userForm.status}
                    onChange={(e) => setUserForm((f) => ({ ...f, status: e.target.value }))}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : currentTheme.surface,
                      color: currentTheme.text,
                      outline: 'none',
                    }}
                  >
                    <option value="active">{t('Active')}</option>
                    <option value="invited">{t('invited')}</option>
                    <option value="disabled">{t('disabled')}</option>
                  </select>
                </label>
              </div>

              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {userSaveError && (
                  <div
                    style={{
                      padding: 10,
                      borderRadius: 12,
                      background: theme === 'light' ? '#FEF2F2' : 'rgba(239, 68, 68, 0.12)',
                      border: `1px solid ${theme === 'light' ? '#FECACA' : 'rgba(239, 68, 68, 0.25)'}`,
                      color: currentTheme.text,
                      fontSize: 13,
                    }}
                  >
                    {userSaveError}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                  <button
                    type="button"
                    onClick={closeUserEditor}
                    disabled={userSaving}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${currentTheme.border}`,
                      background: theme === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.04)',
                      color: currentTheme.text,
                      cursor: userSaving ? 'not-allowed' : 'pointer',
                      opacity: userSaving ? 0.7 : 1,
                    }}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={
                      userSaving
                      || !String(userForm.name || '').trim()
                      || !String(userForm.email || '').trim()
                    }
                    onClick={saveUser}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: 'none',
                      background: theme === 'light' ? '#4F46E5' : '#6366F1',
                      color: '#fff',
                      cursor:
                        userSaving
                        || !String(userForm.name || '').trim()
                        || !String(userForm.email || '').trim()
                          ? 'not-allowed'
                          : 'pointer',
                      opacity:
                        userSaving
                        || !String(userForm.name || '').trim()
                        || !String(userForm.email || '').trim()
                          ? 0.7
                          : 1,
                    }}
                  >
                    {userSaving ? `${t('save')}…` : t('save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

